<script lang="ts">
    import { onDestroy } from 'svelte'

    import { gettext as _ } from 'hydria/services/gettext'
    import { simpleFetch, type SimpleFetchResult } from 'hydria/services/session'
    import { sanchor, sgoto, spath } from 'hydria/svelte_helpers'

    import { d3, nv, nvReady } from 'wdom/utils/nvd'

    import LoadingPattern from 'hydria/components/LoadingPattern.svelte'

    // const UPDATE_TIMEOUT_MS = 293 * 1000

    const STATUSES = [
        { status: 'final', label: 'Final', graph: false, table: false },
        { status: 'unpaid', label: 'Non-payé', graph: '#8c3dc7', table: true },
        { status: 'quotation', label: 'Soumission', graph: '#d4b2f5', table: true },
        { status: 'ready', label: 'Prêt', graph: '#b273e8', table: false },
        { status: 'draft', label: 'Brouillon', graph: '#1c739d', table: false },
        { status: 'paid', label: 'Payé', graph: '#002951', table: false },
        { status: 'processing', label: 'En traitement', graph: '#78e5ff', table: false },
        { status: 'deleted', label: 'Effacé', graph: false, table: false },
    ]

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    let chart: any
    let chartSvg: any

    interface GData {
        label: string
        status: string
        color: string | boolean
        count: number
    }
    let gdata: GData[] = []

    interface TableStatusEntry {
        label: string
        status: string
        count: number
    }
    let ecomStatusTable: TableStatusEntry[] = []

    let loading = $state(true)
    let updating = $state(false)
    let error = $state(false)
    let eventTimer: ReturnType<typeof setTimeout> | undefined = undefined

    let innerWidth: number = $state(0)
    function handleResize(w: number) {
        if (chart && chart.update) chart.update()
    }

    $effect(() => {
        handleResize(innerWidth)
    })

    onDestroy(() => {
        if (eventTimer) {
            try {
                clearTimeout(eventTimer)
            } catch {}
            eventTimer = undefined
        }
        if (chartSvg) {
            if (chart.tooltip && chart.tooltip.id) d3.select(`#${chart.tooltip.id()}`).remove()
            chartSvg.remove()
        }
    })

    if ($nvReady) load()

    async function load() {
        let r: SimpleFetchResult
        if (!loading) updating = true
        error = false

        try {
            r = await simpleFetch('GET', '/json/ecom/1/status-counts')
        } catch (e) {
            console.error('Failed to load task stats:', e)
            error = true
            loading = false
            updating = false
            return
        }

        gdata = []

        for (const s of STATUSES) {
            if (r.data.counts[s.status] == null) r.data.counts[s.status] = 0

            if (s.table)
                ecomStatusTable.push({
                    label: s.label,
                    status: s.status,
                    count: r.data.counts[s.status],
                })

            if (s.graph)
                gdata.push({
                    label: s.label,
                    status: s.status,
                    color: s.graph,
                    count: r.data.counts[s.status],
                })
        }

        nv.addGraph(() => {
            chart = nv.models
                .pieChart()
                .x((d) => d.label)
                .y((d) => d.count)
                // .width(250)
                // .height(300)
                .id(
                    Math.random()
                        .toString(36)
                        .slice(2, 2 + 15),
                )
            chart
                .margin({
                    top: 100,
                    right: 10,
                    bottom: 15,
                    left: 10,
                })
                .donut(false)
                .donutRatio(0.35)
                .labelThreshold(0.002)
                .labelType('value')
                .labelsOutside(true)
                .showLabels(true)
                .showLegend(true)

            chart.legend.margin({
                top: 5,
                right: 10,
                bottom: 20,
                left: 10,
            })

            chart.pie.dispatch.on('elementClick', (e) => {
                sgoto(`/ecom/orders?status=${e.data.status}`)
            })

            chartSvg = d3.select('#order-stat-graph')
            chartSvg.datum(gdata).call(chart)
        })

        loading = false
        updating = false
    }
</script>

<svelte:window bind:innerWidth />

<div class="card">
    <div class="card-title text-primary">
        <span>{$_('Order status counts')}</span>
        {#if updating}
            <span><div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div></span>
        {/if}
    </div>

    {#if loading}
        <div class="card-body d-flex gap-3">
            <div style="flex: 1 0 0">
                <LoadingPattern pattern={['x']} />
            </div>
            <div>{$_('Loading…')}</div>
            <div style="flex: 1 0 0">
                <LoadingPattern pattern={['x']} />
            </div>
        </div>
    {:else if error}
        <div class="card-body">
            <div class="text-danger">Les commandes n’ont pas pu être chargées.</div>
        </div>
    {:else}
        <div class="card-body pb-0">
            <svg id="order-stat-graph" style="height: 275px; width: 100%" class="nvd3-svg" />
        </div>

        <div class="card-body mb-0">
            <table class="table mb-0">
                <tbody>
                    {#each ecomStatusTable as i}
                        <tr>
                            <td><a use:sanchor href={spath(`/ecom/orders?status=${i.status}`)}>{i.label}</a></td>
                            <td style="text-align: right; font-feature-settings: 'ss01' 1, 'tnum' 1;"><a use:sanchor href={spath(`/ecom/orders?status=${i.status}`)}>{i.count}</a></td>
                        </tr>
                    {/each}
                </tbody>
            </table>
        </div>
    {/if}
</div>
